import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button, Input } from './ui';
import { Music, PlusCircle, X } from 'lucide-react';
import { getEmbedUrl } from '../utils/embedding';
import MediaEmbed from './MediaEmbed';
import { memoryService, NewMemoryData } from '../services/MemoryService';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  margin: 8px 0;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px; /* Add a small gap between input and error */
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.status.error};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  padding-left: 4px;
  height: 1.2em; /* Reserve space to prevent layout shifts */
`;

const AddMediaButton = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  border: 1px dashed ${({ theme, hasError }) =>
    hasError ? theme.colors.status.error : theme.colors.border.primary};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.DEFAULT};
    color: ${({ theme }) => theme.colors.primary.DEFAULT};
  }
`;

const ImageUploadOptions = styled.div`
  display: flex;
  gap: 16px;
`;

const SongInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const ImageUploadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`;

interface AddMemoryFormProps {
  initialTitle?: string;
  initialContent?: string;
  initialImageUrl?: string;
  initialSongUrl?: string;
  onSuccess: () => void;
}

const AddMemoryForm: React.FC<AddMemoryFormProps> = ({
  initialTitle = '',
  initialContent = '',
  initialImageUrl = '',
  initialSongUrl = '',
  onSuccess,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [songUrl, setSongUrl] = useState(initialSongUrl);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showSongInput, setShowSongInput] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);

  const embedUrl = useMemo(() => getEmbedUrl(songUrl), [songUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { title?: string; content?: string } = {};
    if (!title) newErrors.title = 'Title is required.';
    if (!content) newErrors.content = 'Text Message is required.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const memoryData: NewMemoryData = {
      title,
      content,
      type: 'message', // Default type
      imageUrl: imageUrl || undefined,
      musicUrl: songUrl || undefined,
    };

    if (imageUrl) memoryData.type = 'photo';
    if (songUrl) memoryData.type = 'music';

    try {
      await memoryService.createMemory(memoryData);
      onSuccess();
    } catch (error) {
      console.error("Failed to create memory:", error);
      // Optionally, set a form-level error message to display to the user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePingAndJoin = async () => {
    setErrors({}); // Clear errors from the other form submission
    setIsSubmitting(true);

    const pingData: NewMemoryData = {
      title: "Chikishoun.tv",
      content: "Let's join the call!",
      type: 'message',
    };

    try {
      // This will create a memory. If a notification-only ping is desired,
      // a different API endpoint might be needed.
      await memoryService.createMemory(pingData);
      window.location.href = 'https://meet.google.com/ipx-pnme-jan';
    } catch (error) {
      console.error("Failed to send ping and join:", error);
      // Handle error, e.g., show a notification to the user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setImageUrl(previewUrl); // Show preview immediately
      setShowImageOptions(false);
      setIsUploading(true);

      try {
        const { url } = await memoryService.uploadImage(file);
        setImageUrl(url); // Replace preview with permanent URL
      } catch (error) {
        console.error("Failed to upload image:", error);
        // TODO: Show an error to the user
        setImageUrl(''); // Clear image on failure
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      {isSubmitting && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'orange',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            zIndex: 1000,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
          }}
        >
          Sending...
        </div>
      )}
      <InputContainer>
        <Input
          label="Title"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          placeholder="What's the occasion?"
          hasError={!!errors.title}
        />
        <ErrorMessage>{errors.title || ''}</ErrorMessage>
      </InputContainer>
      <InputContainer>
        <Input
          label="Text Message"
          value={content}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContent(e.target.value)}
          placeholder="Write your message..."
          hasError={!!errors.content}
        />
        <ErrorMessage>{errors.content || ''}</ErrorMessage>
      </InputContainer>

      {!imageUrl && !showImageOptions && (
        <AddMediaButton onClick={() => setShowImageOptions(true)}>
          <PlusCircle size={16} />
          <span>Add an image</span>
        </AddMediaButton>
      )}

      {showImageOptions && (
        <ImageUploadOptions>
          <Button type="button" onClick={() => imageInputRef.current?.click()}>
            Upload Image
          </Button>
          <Button type="button" onClick={() => cameraInputRef.current?.click()}>
            Take a Picture
          </Button>
          <Button type="button" variant="ghost" onClick={() => setShowImageOptions(false)}>
            Cancel
          </Button>
        </ImageUploadOptions>
      )}

      <input
        type="file"
        accept="image/*"
        ref={imageInputRef}
        onChange={handleImageChange}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={cameraInputRef}
        onChange={handleImageChange}
        style={{ display: 'none' }}
      />

      {imageUrl && (
        <ImagePreviewContainer>
          <img src={imageUrl} alt="Memory preview" style={{ width: '100%', borderRadius: '8px' }} />
          {isUploading && (
            <ImageUploadOverlay>
              <span>Uploading...</span>
            </ImageUploadOverlay>
          )}
          <RemoveImageButton type="button" onClick={handleRemoveImage} disabled={isUploading}>
            <X size={16} />
          </RemoveImageButton>
        </ImagePreviewContainer>
      )}
      
      {!showSongInput ? (
         <AddMediaButton onClick={() => setShowSongInput(true)}>
            <PlusCircle size={16} />
            <span>Add a song/video link</span>
        </AddMediaButton>
      ) : (
        <SongInputContainer>
          <div style={{ flexGrow: 1 }}>
            <Input
              label="Song/Media URL"
              value={songUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSongUrl(e.target.value)}
              placeholder="Add a song from Spotify, YouTube, etc."
              leftIcon={<Music size={16} />}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setShowSongInput(false);
              setSongUrl('');
            }}
          >
            Cancel
          </Button>
        </SongInputContainer>
      )}

      {embedUrl && <MediaEmbed embedUrl={embedUrl} />}
      <Button type="submit" disabled={isSubmitting || isUploading}>
        {isSubmitting ? 'Sending...' : 'Ping and Add Memory'}
      </Button>
      <Divider />
      <Button
        type="button"
        variant="outline"
        onClick={handlePingAndJoin}
        disabled={isSubmitting || isUploading}
      >
        Ping and Join Chikishoun.tv
      </Button>
    </FormContainer>
  );
};

export default AddMemoryForm;
